import type { HouseholdType, Inputs } from '../calc/types';
import { DEFAULT_INPUTS } from '../calc/presets';

/**
 * Inputs <-> location.hash, so a scenario is shareable and bookmarkable
 * without any server or database. Rates travel as percentages to keep the
 * URL readable: #c=GBP&a=30&r=67&p=50000&s1y=10&s1c=10000&s1i=1&...
 */
const KEYS = {
  currency: 'c',
  householdType: 'h',
  currentAge: 'a',
  retirementAge: 'r',
  currentPot: 'p',
  stage1Contribution: 's1c',
  stage1Inflation: 's1i',
  stage2Years: 's2y',
  stage2Contribution: 's2c',
  stage2Inflation: 's2i',
  stage3Years: 's3y',
  stage3Contribution: 's3c',
  stage3Inflation: 's3i',
  growthRate: 'g',
  inflationRate: 'i',
  withdrawalRate: 'w',
} as const;

export function encodeInputs(input: Inputs): string {
  const [s1, s2, s3] = input.stages;
  const params = new URLSearchParams();
  params.set(KEYS.currency, input.currency);
  params.set(KEYS.householdType, input.householdType);
  params.set(KEYS.currentAge, String(input.currentAge));
  params.set(KEYS.retirementAge, String(input.retirementAge));
  params.set(KEYS.currentPot, trim(input.currentPot));
  params.set(KEYS.stage1Contribution, trim(s1.annualContribution));
  params.set(KEYS.stage1Inflation, s1.growsWithInflation ? '1' : '0');
  params.set(KEYS.stage2Years, String(s2.years));
  params.set(KEYS.stage2Contribution, trim(s2.annualContribution));
  params.set(KEYS.stage2Inflation, s2.growsWithInflation ? '1' : '0');
  params.set(KEYS.stage3Years, String(s3.years));
  params.set(KEYS.stage3Contribution, trim(s3.annualContribution));
  params.set(KEYS.stage3Inflation, s3.growsWithInflation ? '1' : '0');
  params.set(KEYS.growthRate, trim(input.growthRate * 100));
  params.set(KEYS.inflationRate, trim(input.inflationRate * 100));
  params.set(KEYS.withdrawalRate, trim(input.withdrawalRate * 100));
  return params.toString();
}

/** Anything missing or unparseable falls back to the default, never to NaN. */
export function decodeInputs(hash: string): Inputs {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  if ([...params.keys()].length === 0) return { ...DEFAULT_INPUTS };

  const [d1, d2, d3] = DEFAULT_INPUTS.stages;

  return {
    currency: params.get(KEYS.currency) ?? DEFAULT_INPUTS.currency,
    householdType: household(params.get(KEYS.householdType), DEFAULT_INPUTS.householdType),
    currentAge: num(params.get(KEYS.currentAge), DEFAULT_INPUTS.currentAge),
    retirementAge: num(params.get(KEYS.retirementAge), DEFAULT_INPUTS.retirementAge),
    currentPot: num(params.get(KEYS.currentPot), DEFAULT_INPUTS.currentPot),
    stages: [
      {
        years: 0,
        annualContribution: num(params.get(KEYS.stage1Contribution), d1.annualContribution),
        growsWithInflation: bool(params.get(KEYS.stage1Inflation), d1.growsWithInflation),
      },
      {
        years: num(params.get(KEYS.stage2Years), d2.years),
        annualContribution: num(params.get(KEYS.stage2Contribution), d2.annualContribution),
        growsWithInflation: bool(params.get(KEYS.stage2Inflation), d2.growsWithInflation),
      },
      {
        years: num(params.get(KEYS.stage3Years), d3.years),
        annualContribution: num(params.get(KEYS.stage3Contribution), d3.annualContribution),
        growsWithInflation: bool(params.get(KEYS.stage3Inflation), d3.growsWithInflation),
      },
    ],
    growthRate: pct(params.get(KEYS.growthRate), DEFAULT_INPUTS.growthRate),
    inflationRate: pct(params.get(KEYS.inflationRate), DEFAULT_INPUTS.inflationRate),
    withdrawalRate: pct(params.get(KEYS.withdrawalRate), DEFAULT_INPUTS.withdrawalRate),
  };
}

const STORAGE_KEY = 'pensionApp.inputs.v2';

/** localStorage is a convenience, never a dependency — it throws in some browsers. */
export function saveInputs(input: Inputs): void {
  try {
    localStorage.setItem(STORAGE_KEY, encodeInputs(input));
  } catch {
    /* private mode, blocked cookies — carry on */
  }
}

export function loadInputs(): Inputs | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? decodeInputs(stored) : null;
  } catch {
    return null;
  }
}

function num(raw: string | null, fallback: number): number {
  if (raw === null || raw.trim() === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

function pct(raw: string | null, fallback: number): number {
  if (raw === null || raw.trim() === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n / 100 : fallback;
}

function bool(raw: string | null, fallback: boolean): boolean {
  return raw === null ? fallback : raw === '1';
}

function household(raw: string | null, fallback: HouseholdType): HouseholdType {
  return raw === 'individual' || raw === 'couple' ? raw : fallback;
}

/** Keeps the URL tidy: 5 not 5.000000000000001. */
function trim(n: number): string {
  return String(Number(n.toFixed(4)));
}
